import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  Printer, FileSpreadsheet, FileText, Share2, RefreshCw, X,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function LedgerReportDialog({ 
  open, 
  onClose, 
  clientId, 
  clientName,
  isHistorical = false,
  mondayFinalIds = []
}) {
  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 20;
  const printRef = useRef(null);

  useEffect(() => {
    if (open && clientId) {
      fetchLedger();
    }
  }, [open, clientId, JSON.stringify(mondayFinalIds)]);

  const fetchLedger = async () => {
    try {
      setLoading(true);
      
      let url = `${API_URL}/api/settlements/ledger/${clientId}`;
      
      // If historical with multiple monday final IDs, use combined endpoint
      if (isHistorical && mondayFinalIds && mondayFinalIds.length > 0) {
        if (mondayFinalIds.length === 1) {
          url = `${API_URL}/api/settlements/monday-final/${mondayFinalIds[0]}/ledger`;
        } else {
          // Multiple selections - use combined endpoint
          url = `${API_URL}/api/settlements/monday-finals/combined-ledger?ids=${mondayFinalIds.join(',')}`;
        }
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLedgerData(data);
        setCurrentPage(1);
      } else {
        toast.error('Failed to fetch ledger data');
      }
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Error loading ledger');
    } finally {
      setLoading(false);
    }
  };

  // Pagination
  const totalPages = ledgerData ? Math.ceil(ledgerData.entries.length / entriesPerPage) : 1;
  const paginatedEntries = ledgerData?.entries.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  ) || [];

  // Print functionality
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Ledger - ${clientName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { text-align: center; margin-bottom: 5px; }
            .header-info { text-align: center; color: #666; margin-top: 0; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #333; padding: 8px; text-align: center; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .text-left { text-align: left; }
            .text-right { text-align: right; }
            .dr { color: #dc2626; }
            .cr { color: #16a34a; }
            .balance-positive { color: #16a34a; }
            .balance-negative { color: #dc2626; }
            .footer { margin-top: 20px; display: flex; justify-content: space-between; border-top: 2px solid #333; padding-top: 10px; }
            .historical-badge { background: #fef3c7; color: #92400e; padding: 4px 12px; border-radius: 4px; display: inline-block; margin-bottom: 10px; }
            .page-footer { margin-top: 30px; text-align: center; color: #666; font-size: 12px; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${isHistorical ? `<div class="historical-badge">Historical - Locked Period (${ledgerData?.monday_final_date || ''})</div>` : ''}
          <h1>Ledger: ${clientName}</h1>
          <div class="header-info">
            <strong>Old Balance:</strong> ${(ledgerData?.old_balance || 0).toFixed(2)} | 
            <strong>As on:</strong> ${ledgerData?.report_date || format(new Date(), 'dd-MM-yyyy')}
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 100px;">Date</th>
                <th class="text-left">Particular / Comment</th>
                <th style="width: 120px;">Dr (-)</th>
                <th style="width: 120px;">Cr (+)</th>
                <th style="width: 120px;">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${ledgerData?.entries.map(entry => `
                <tr>
                  <td>${entry.date || ''}</td>
                  <td class="text-left">${entry.particular || ''}</td>
                  <td class="text-right dr">${entry.dr > 0 ? entry.dr.toFixed(2) : ''}</td>
                  <td class="text-right cr">${entry.cr > 0 ? entry.cr.toFixed(2) : ''}</td>
                  <td class="text-right ${entry.balance >= 0 ? 'balance-positive' : 'balance-negative'}">${entry.balance.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="font-weight: bold; background-color: #f5f5f5;">
                <td colspan="2" class="text-right">Totals:</td>
                <td class="text-right dr">${ledgerData?.total_dr?.toFixed(2) || '0.00'}</td>
                <td class="text-right cr">${ledgerData?.total_cr?.toFixed(2) || '0.00'}</td>
                <td class="text-right ${ledgerData?.closing_balance >= 0 ? 'balance-positive' : 'balance-negative'}">${ledgerData?.closing_balance?.toFixed(2) || '0.00'}</td>
              </tr>
            </tfoot>
          </table>
          <div class="footer">
            <div><strong>Old Balance:</strong> ${(ledgerData?.old_balance || 0).toFixed(2)}</div>
            <div><strong>Closing Balance:</strong> ${ledgerData?.closing_balance?.toFixed(2) || '0.00'}</div>
          </div>
          <div class="page-footer">
            Generated on ${format(new Date(), 'dd-MM-yyyy HH:mm')} | YellowGold Pro
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!ledgerData) return;
    
    const worksheetData = [
      ['Ledger: ' + clientName],
      ['Old Balance: ' + (ledgerData.old_balance || 0).toFixed(2) + ' | As on: ' + (ledgerData.report_date || format(new Date(), 'dd-MM-yyyy'))],
      [''],
      ['Date', 'Particular / Comment', 'Dr (-)', 'Cr (+)', 'Balance'],
      ...ledgerData.entries.map(entry => [
        entry.date,
        entry.particular,
        entry.dr > 0 ? entry.dr : '',
        entry.cr > 0 ? entry.cr : '',
        entry.balance
      ]),
      [''],
      ['', 'Totals:', ledgerData.total_dr, ledgerData.total_cr, ledgerData.closing_balance],
      [''],
      ['Old Balance:', ledgerData.old_balance || 0],
      ['Closing Balance:', ledgerData.closing_balance]
    ];
    
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ledger');
    
    const fileName = `Ledger_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${format(new Date(), 'yyyyMMdd')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success('Excel exported successfully');
  };

  // Export to PDF
  const handleExportPDF = () => {
    if (!ledgerData) return;
    
    const doc = new jsPDF('portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Ledger: ${clientName}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Old Balance: ${(ledgerData.old_balance || 0).toFixed(2)} | As on: ${ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')}`, pageWidth / 2, 22, { align: 'center' });
    
    if (isHistorical) {
      doc.setFontSize(10);
      doc.setTextColor(146, 64, 14);
      doc.text(`Historical - Locked Period (${ledgerData.monday_final_date || ''})`, pageWidth / 2, 28, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
    
    // Table
    const tableData = ledgerData.entries.map(entry => [
      entry.date,
      entry.particular,
      entry.dr > 0 ? entry.dr.toFixed(2) : '',
      entry.cr > 0 ? entry.cr.toFixed(2) : '',
      entry.balance.toFixed(2)
    ]);
    
    // Add totals row
    tableData.push([
      '',
      'Totals:',
      ledgerData.total_dr.toFixed(2),
      ledgerData.total_cr.toFixed(2),
      ledgerData.closing_balance.toFixed(2)
    ]);
    
    doc.autoTable({
      startY: isHistorical ? 35 : 28,
      head: [['Date', 'Particular / Comment', 'Dr (-)', 'Cr (+)', 'Balance']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 25, halign: 'right' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 25, halign: 'right' }
      },
      didParseCell: (data) => {
        // Color Dr column red
        if (data.column.index === 2 && data.cell.raw) {
          data.cell.styles.textColor = [220, 38, 38];
        }
        // Color Cr column green
        if (data.column.index === 3 && data.cell.raw) {
          data.cell.styles.textColor = [22, 163, 74];
        }
        // Color Balance based on value
        if (data.column.index === 4 && data.cell.raw) {
          const val = parseFloat(data.cell.raw);
          if (val < 0) {
            data.cell.styles.textColor = [220, 38, 38];
          } else {
            data.cell.styles.textColor = [22, 163, 74];
          }
        }
      }
    });
    
    // Footer
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Opening Balance: ${ledgerData.opening_balance.toFixed(2)}`, 14, finalY);
    doc.text(`Closing Balance: ${ledgerData.closing_balance.toFixed(2)}`, pageWidth - 60, finalY);
    
    // Page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
      doc.text(`Generated: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, 14, doc.internal.pageSize.getHeight() - 10);
    }
    
    const fileName = `Ledger_${clientName.replace(/[^a-zA-Z0-9]/g, '_')}_${format(new Date(), 'yyyyMMdd')}.pdf`;
    doc.save(fileName);
    toast.success('PDF exported successfully');
  };

  // Share state for modal
  const [shareDialog, setShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  // Generate PDF Blob for sharing
  const generatePDFBlob = () => {
    if (!ledgerData) return null;
    
    const doc = new jsPDF('portrait');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Ledger: ${clientName}`, pageWidth / 2, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Old Balance: ${(ledgerData.old_balance || 0).toFixed(2)} | As on: ${ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')}`, pageWidth / 2, 22, { align: 'center' });
    
    if (isHistorical) {
      doc.setFontSize(10);
      doc.setTextColor(146, 64, 14);
      doc.text(`Historical - Locked Period (${ledgerData.monday_final_date || ''})`, pageWidth / 2, 28, { align: 'center' });
      doc.setTextColor(0, 0, 0);
    }
    
    const tableData = ledgerData.entries.map(entry => [
      entry.date,
      entry.particular,
      entry.dr > 0 ? entry.dr.toFixed(2) : '',
      entry.cr > 0 ? entry.cr.toFixed(2) : '',
      entry.balance.toFixed(2)
    ]);
    
    tableData.push([
      '',
      'Totals:',
      ledgerData.total_dr.toFixed(2),
      ledgerData.total_cr.toFixed(2),
      ledgerData.closing_balance.toFixed(2)
    ]);
    
    doc.autoTable({
      startY: isHistorical ? 35 : 28,
      head: [['Date', 'Particular / Comment', 'Dr (-)', 'Cr (+)', 'Balance']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [100, 100, 100], textColor: 255, fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 25, halign: 'right' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 25, halign: 'right' }
      }
    });
    
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Old Balance: ${(ledgerData.old_balance || 0).toFixed(2)}`, 14, finalY);
    doc.text(`Closing Balance: ${ledgerData.closing_balance.toFixed(2)}`, pageWidth - 60, finalY);
    
    return doc.output('blob');
  };

  // Share functionality - opens share options dialog
  const handleShare = () => {
    if (!ledgerData) return;
    setShareDialog(true);
  };

  // Share via WhatsApp
  const handleShareWhatsApp = async () => {
    if (!ledgerData) return;
    
    // Generate summary text
    const summary = `*Ledger: ${clientName}*\n` +
      `Old Balance: ${(ledgerData.old_balance || 0).toFixed(2)}\n` +
      `Closing Balance: ${ledgerData.closing_balance.toFixed(2)}\n` +
      `Total Dr: ${ledgerData.total_dr.toFixed(2)}\n` +
      `Total Cr: ${ledgerData.total_cr.toFixed(2)}\n` +
      `As on: ${ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')}`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(summary)}`;
    window.open(whatsappUrl, '_blank');
    
    // Also download PDF for manual attachment
    handleExportPDF();
    toast.success('PDF downloaded. Attach it to your WhatsApp message.');
    setShareDialog(false);
  };

  // Share via Email
  const handleShareEmail = () => {
    if (!ledgerData) return;
    
    const subject = `Ledger: ${clientName}`;
    const body = `Ledger for ${clientName}\n\n` +
      `Old Balance: ${(ledgerData.old_balance || 0).toFixed(2)}\n` +
      `Closing Balance: ${ledgerData.closing_balance.toFixed(2)}\n` +
      `Total Debit: ${ledgerData.total_dr.toFixed(2)}\n` +
      `Total Credit: ${ledgerData.total_cr.toFixed(2)}\n\n` +
      `As on: ${ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')}\n\n` +
      `Please find the detailed ledger attached.`;
    
    const mailtoUrl = `mailto:${shareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
    
    // Also download PDF for manual attachment
    handleExportPDF();
    toast.success('PDF downloaded. Attach it to your email.');
    setShareDialog(false);
    setShareEmail('');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] p-0 gap-0 overflow-hidden">
        {/* Header - Ledger: {Account Name}, Old Balance, Report Date */}
        <div className="bg-gradient-to-r from-amber-100 to-amber-200 dark:from-amber-900 dark:to-amber-800 border-b px-4 py-3">
          <DialogHeader className="space-y-1">
            <DialogTitle className="text-lg font-semibold text-amber-800 dark:text-amber-100 flex items-center justify-between">
              <span>Ledger: {clientName}</span>
              {isHistorical && (
                <span className="text-xs px-2 py-1 bg-amber-600 text-white rounded">
                  Historical - Locked Period
                </span>
              )}
            </DialogTitle>
            {ledgerData && (
              <div className="flex items-center gap-4 text-sm text-amber-700 dark:text-amber-200">
                <span>Old Balance: <span className="font-mono font-semibold">{(ledgerData.old_balance || 0).toFixed(2)}</span></span>
                <span>|</span>
                <span>As on {ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')}</span>
              </div>
            )}
          </DialogHeader>
        </div>

        {/* Action Buttons */}
        <div className="border-b bg-slate-50 dark:bg-slate-900/50 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                className="h-8 gap-1"
              >
                <Printer className="h-3.5 w-3.5" />
                Print
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportExcel}
                className="h-8 gap-1"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Excel
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportPDF}
                className="h-8 gap-1"
              >
                <FileText className="h-3.5 w-3.5" />
                PDF
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleShare}
                className="h-8 gap-1"
              >
                <Share2 className="h-3.5 w-3.5" />
                Share
              </Button>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onClose(false)}
              className="h-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 bg-white dark:bg-slate-950" ref={printRef}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : ledgerData ? (
            <div className="space-y-4">
              {/* Historical badge */}
              {isHistorical && ledgerData.monday_final_date && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-4 py-2 text-center">
                  <span className="text-amber-800 dark:text-amber-200 font-medium">
                    Monday Final: {ledgerData.monday_final_date}
                  </span>
                </div>
              )}

              {/* Ledger Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold w-24">Date</th>
                      <th className="px-3 py-2 text-left font-semibold">Particular / Comment</th>
                      <th className="px-3 py-2 text-right font-semibold w-28">Dr (-)</th>
                      <th className="px-3 py-2 text-right font-semibold w-28">Cr (+)</th>
                      <th className="px-3 py-2 text-right font-semibold w-28">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {paginatedEntries.map((entry, idx) => (
                      <tr 
                        key={idx} 
                        className={cn(
                          "hover:bg-slate-50 dark:hover:bg-slate-900/50",
                          entry.is_opening && "bg-blue-50 dark:bg-blue-900/20"
                        )}
                      >
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                          {entry.date}
                        </td>
                        <td className="px-3 py-2">
                          {entry.particular}
                          {entry.is_locked && (
                            <span className="ml-2 text-xs px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">
                              Locked
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-red-600 dark:text-red-400">
                          {entry.dr > 0 ? entry.dr.toFixed(2) : ''}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-600 dark:text-emerald-400">
                          {entry.cr > 0 ? entry.cr.toFixed(2) : ''}
                        </td>
                        <td className={cn(
                          "px-3 py-2 text-right font-mono font-medium",
                          entry.balance >= 0 
                            ? "text-emerald-600 dark:text-emerald-400" 
                            : "text-red-600 dark:text-red-400"
                        )}>
                          {entry.balance.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 dark:bg-slate-800 font-semibold">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-right">Totals:</td>
                      <td className="px-3 py-2 text-right font-mono text-red-600 dark:text-red-400">
                        {ledgerData.total_dr?.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-600 dark:text-emerald-400">
                        {ledgerData.total_cr?.toFixed(2)}
                      </td>
                      <td className={cn(
                        "px-3 py-2 text-right font-mono",
                        ledgerData.closing_balance >= 0 
                          ? "text-emerald-600 dark:text-emerald-400" 
                          : "text-red-600 dark:text-red-400"
                      )}>
                        {ledgerData.closing_balance?.toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-2">
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No ledger data available
            </div>
          )}
        </div>

        {/* Footer with Old Balance and Closing Balance - NO Current Balance */}
        {ledgerData && (
          <div className="border-t bg-slate-50 dark:bg-slate-900/50 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-sm text-muted-foreground">Old Balance: </span>
                  <span className={cn(
                    "font-mono font-semibold",
                    (ledgerData.old_balance || 0) >= 0 
                      ? "text-emerald-600 dark:text-emerald-400" 
                      : "text-red-600 dark:text-red-400"
                  )}>
                    {(ledgerData.old_balance || 0).toFixed(2)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">Closing Balance: </span>
                  <span className={cn(
                    "font-mono font-semibold text-lg",
                    ledgerData.closing_balance >= 0 
                      ? "text-emerald-600 dark:text-emerald-400" 
                      : "text-red-600 dark:text-red-400"
                  )}>
                    {ledgerData.closing_balance?.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                As on {ledgerData.report_date || format(new Date(), 'dd-MM-yyyy')} | Page {currentPage} of {totalPages}
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Share Options Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Share Ledger
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Choose how you want to share the ledger for <strong>{clientName}</strong>
            </p>
            
            {/* WhatsApp Option */}
            <Button
              variant="outline"
              className="w-full justify-start gap-3 h-12"
              onClick={handleShareWhatsApp}
            >
              <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="font-medium">Share via WhatsApp</div>
                <div className="text-xs text-muted-foreground">Downloads PDF & opens WhatsApp</div>
              </div>
            </Button>
            
            {/* Email Option */}
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-12"
                onClick={handleShareEmail}
              >
                <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-medium">Share via Email</div>
                  <div className="text-xs text-muted-foreground">Downloads PDF & opens email client</div>
                </div>
              </Button>
              <input
                type="email"
                placeholder="Enter recipient email (optional)"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-background"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShareDialog(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
